import { ModalForm, type ProColumns } from "@ant-design/pro-components";
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import {
  AdminPickerWorkspace,
  type AdminPickerWorkspaceTableProps,
} from "./admin-picker-workspace";
import { usePickerRowDoubleClick, withPickerRowDoubleClick } from "./picker-row-double-click";

type CatalogPickerTableProps<T extends object, U extends object> = AdminPickerWorkspaceTableProps<
  T,
  U
>;
type CatalogPickerModalFormProps = ComponentProps<typeof ModalForm<Record<string, never>>>;

export type CatalogPickerModalProps<T extends object, U extends object = Record<string, unknown>> = {
  title: ReactNode;
  submitText: string;
  cancelText: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modalProps?: CatalogPickerModalFormProps["modalProps"];
  submitButtonProps?: { loading?: boolean; disabled?: boolean };
  resetButtonProps?: { onClick?: () => void };
  columns: readonly ProColumns<T>[];
  persistenceKey: string;
  onSubmit: () => Promise<boolean>;
  rowKey: string | ((record: T) => string | number);
  selectedRowKeys: readonly (string | number)[];
  onSelectionChange: (keys: Array<string | number>, rows: T[]) => void;
  emptyText: ReactNode;
  width?: number | string;
  rowSelectionType?: "radio" | "checkbox";
  getCheckboxProps?: (record: T) => { disabled?: boolean };
  preserveSelectedRowKeys?: boolean;
  tableLayout?: "auto" | "fixed";
  showToolbarOptions?: boolean;
  header?: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  showTableSkeleton?: boolean;
  tableProps?: CatalogPickerTableProps<T, U>;
  sidebar?: ReactNode;
  sidebarWidth?: number | string;
};

const catalogPickerModalSidebarLayoutStyle: CSSProperties = {
  display: "grid",
  gap: 16,
  alignItems: "start",
};

const catalogPickerModalPanelStyle: CSSProperties = {
  minWidth: 0,
};

export function CatalogPickerModal<T extends object, U extends object = Record<string, unknown>>({
  title,
  submitText,
  cancelText,
  open,
  onOpenChange,
  modalProps,
  submitButtonProps,
  resetButtonProps,
  columns,
  persistenceKey,
  onSubmit,
  rowKey,
  selectedRowKeys,
  onSelectionChange,
  emptyText,
  width = 960,
  rowSelectionType = "radio",
  getCheckboxProps,
  preserveSelectedRowKeys,
  tableLayout = "auto",
  showToolbarOptions = true,
  header,
  footerLeft,
  footerRight,
  showTableSkeleton,
  tableProps,
  sidebar,
  sidebarWidth = 220,
}: CatalogPickerModalProps<T, U>) {
  const handleResetClick = () => {
    resetButtonProps?.onClick?.();
    onOpenChange(false);
  };
  const rowDoubleClick = usePickerRowDoubleClick({
    open,
    rowKey,
    rowSelectionType,
    selectedRowKeys,
    onSelectionChange,
    ...(getCheckboxProps ? { getCheckboxProps } : {}),
    onSubmit,
    onClose: () => onOpenChange(false),
    blocked: submitButtonProps?.loading === true || submitButtonProps?.disabled === true,
  });
  const tableOnRow = tableProps?.onRow;

  const tableNode = (
    <AdminPickerWorkspace<T, U>
      columns={columns}
      {...(header === undefined ? {} : { header })}
      rowKey={rowKey}
      persistenceKey={persistenceKey}
      showToolbarOptions={showToolbarOptions}
      emptyText={emptyText}
      tableLayout={tableLayout}
      tableProps={{
        ...(tableProps ?? {}),
        ...(rowDoubleClick.enabled
          ? {
              onRow: withPickerRowDoubleClick(tableOnRow, rowDoubleClick.onRowDoubleClick),
            }
          : {}),
      }}
      selectedRowKeys={selectedRowKeys}
      onSelectionChange={onSelectionChange}
      rowSelectionType={rowSelectionType}
      {...(getCheckboxProps ? { getCheckboxProps } : {})}
      {...(preserveSelectedRowKeys === undefined ? {} : { preserveSelectedRowKeys })}
      {...(footerLeft === undefined ? {} : { footerLeft })}
      {...(footerRight === undefined ? {} : { footerRight })}
      {...(showTableSkeleton === undefined ? {} : { showTableSkeleton })}
    />
  );
  const content = sidebar ? (
    <div
      style={{
        ...catalogPickerModalSidebarLayoutStyle,
        gridTemplateColumns: `${typeof sidebarWidth === "number" ? `${sidebarWidth}px` : sidebarWidth} minmax(0, 1fr)`,
      }}
    >
      <div style={catalogPickerModalPanelStyle}>{sidebar}</div>
      <div style={catalogPickerModalPanelStyle}>{tableNode}</div>
    </div>
  ) : (
    tableNode
  );

  return (
    <ModalForm<Record<string, never>>
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      modalProps={{ destroyOnHidden: true, width, ...modalProps }}
      submitter={{
        searchConfig: { submitText, resetText: cancelText },
        ...(submitButtonProps === undefined ? {} : { submitButtonProps }),
        resetButtonProps: {
          ...resetButtonProps,
          onClick: handleResetClick,
        },
      }}
      onFinish={() => onSubmit()}
    >
      {content}
    </ModalForm>
  );
}
